import React, { useState, useEffect } from "react";

export default function LandingPage() {
  const [scrollY, setScrollY] = useState(0);
  const [activeStory, setActiveStory] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white text-gray-800">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Libre+Baskerville:wght@400;700&display=swap');
        
        
        body {
          font-family: 'Inter', sans-serif;
          -webkit-font-smoothing: antialiased;
        }
        
        .serif {
          font-family: 'Libre Baskerville', serif;
        }
        
        .subtle-underline {
          position: relative;
          display: inline-block;
        }
        
        .subtle-underline::after {
          content: '';
          position: absolute;
          bottom: -2px;
          left: 0;
          right: 0;
          height: 2px;
          background: linear-gradient(to right, transparent, #6366f1, transparent);
          opacity: 0.4;
        }
        
        .story-card {
          transition: all 0.3s ease;
        }
        
        .story-card:hover {
          transform: translateY(-4px);
        }
        
        .fade-in-up {
          opacity: 0;
          animation: fadeInUp 0.8s ease-out forwards;
        }
        
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .stagger-1 { animation-delay: 0.1s; }
        .stagger-2 { animation-delay: 0.2s; }
        .stagger-3 { animation-delay: 0.3s; }
        
        .sticky-progress {
          position: fixed;
          top: 0;
          left: 0;
          height: 2px;
          background: linear-gradient(to right, #6366f1, #8b5cf6);
          z-index: 9999;
          transition: width 0.1s ease-out;
        }
      `}</style>

      {/* Progress bar */}
      <div
        className="sticky-progress"
        style={{
          width: `${(scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100}%`,
        }}
      />

      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 backdrop-blur-lg bg-white/90 border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-6 py-3 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-indigo-500 rounded-lg flex items-center justify-center text-white text-sm font-semibold">
              L
            </div>
            <span className="text-lg font-semibold text-gray-900">Logo</span>
          </div>
          <div className="flex items-center gap-6">
            <a
              href="#why"
              className="text-sm text-gray-600 hover:text-indigo-600 transition"
            >
              Why
            </a>
            <a
              href="#how"
              className="text-sm text-gray-600 hover:text-indigo-600 transition"
            >
              How
            </a>
            <button className="px-4 py-1.5 bg-indigo-600 text-white text-sm rounded-lg font-medium hover:bg-indigo-700 transition">
              Check Loan
            </button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-24 pb-16 px-6 bg-gradient-to-b from-indigo-50/30 to-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block px-3 py-1 bg-indigo-50 rounded-full text-xs font-medium text-indigo-700 mb-4">
              A Common Financial Oversight
            </span>

            <h1 className="serif text-4xl md:text-5xl font-bold mb-4 leading-tight text-gray-900">
              When loan calculations
              <br />
              <span className="text-indigo-600 subtle-underline">
                don’t add up
              </span>
            </h1>

            <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
              Loans run for decades. Rates change, policies evolve, and
              calculations compound. Most borrowers never verify whether those
              changes were applied correctly.
            </p>
          </div>

          {/* Case Patterns */}
          <div className="grid md:grid-cols-2 gap-6 mt-12">
            <div className="p-6 bg-white border border-gray-200 rounded-xl shadow-sm">
              <h3 className="serif text-xl font-semibold mb-2 text-gray-900">
                Case 1: Missed Rate Adjustments
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed mb-3">
                A long-term borrower continued paying the same EMI even after
                multiple rate reductions were announced over several years.
              </p>
              <p className="text-sm font-medium text-indigo-600 mb-4">
                Rate benefits were never passed on.
              </p>
              <div className="pt-4 border-t border-gray-100 flex justify-between">
                <span className="text-xs text-gray-500">
                  Estimated excess paid
                </span>
                <span className="text-lg font-semibold text-indigo-600">
                  ₹4.2L
                </span>
              </div>
            </div>

            <div className="p-6 bg-white border border-gray-200 rounded-xl shadow-sm">
              <h3 className="serif text-xl font-semibold mb-2 text-gray-900">
                Case 2: Silent Tenure Extensions
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed mb-3">
                EMI amounts stayed constant, but loan tenure kept increasing —
                often without clear explanation or borrower consent.
              </p>
              <p className="text-sm font-medium text-indigo-600 mb-4">
                Compounding interest and undocumented charges.
              </p>
              <div className="pt-4 border-t border-gray-100 flex justify-between">
                <span className="text-xs text-gray-500">
                  Estimated excess paid
                </span>
                <span className="text-lg font-semibold text-indigo-600">
                  ₹6.8L
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Problem */}
      <section className="py-16 px-6 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="serif text-3xl md:text-4xl font-bold mb-8 text-gray-900">
            The <span className="text-indigo-600">uncomfortable</span> truth
          </h2>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="p-6 bg-white rounded-xl border border-gray-200">
              <div className="text-3xl mb-3">📊</div>
              <h3 className="text-lg font-semibold mb-2 text-gray-900">
                240+ months
              </h3>
              <p className="text-sm text-gray-600">
                20 years of calculations. Who's checking?
              </p>
            </div>
            <div className="p-6 bg-white rounded-xl border border-gray-200">
              <div className="text-3xl mb-3">📈</div>
              <h3 className="text-lg font-semibold mb-2 text-gray-900">
                12-15 rate changes
              </h3>
              <p className="text-sm text-gray-600">
                Each should trigger recalculation.
              </p>
            </div>
            <div className="p-6 bg-white rounded-xl border border-gray-200">
              <div className="text-3xl mb-3">⚠️</div>
              <h3 className="text-lg font-semibold mb-2 text-gray-900">
                Hidden complexity
              </h3>
              <p className="text-sm text-gray-600">The fine print adds up.</p>
            </div>
          </div>

          <div className="p-6 bg-indigo-50 border-l-4 border-indigo-600 rounded-r-lg text-left">
            <p className="text-base md:text-lg leading-relaxed text-gray-700">
              Banks process millions of loans. You only have{" "}
              <span className="font-semibold text-indigo-700">one</span>. When
              errors happen, they happen quietly and compound over{" "}
              <span className="font-semibold">years</span>.
            </p>
          </div>
        </div>
      </section>

      {/* The Genesis */}
      <section id="why" className="py-20 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
              Why independent loan verification matters
            </h2>
            <p className="text-base text-gray-600 max-w-2xl mx-auto">
              Not because banks are careless — but because loan systems are
              complex, long-term, and rarely reviewed end-to-end.
            </p>
          </div>

          {/* Insight Blocks */}
          <div className="grid md:grid-cols-3 gap-6 mb-16">
            <div className="p-6 border border-gray-200 rounded-xl bg-gray-50">
              <h4 className="font-semibold text-gray-900 mb-2">
                Long-duration products
              </h4>
              <p className="text-sm text-gray-600 leading-relaxed">
                Most loans run for 10–25 years. Small inconsistencies compound
                quietly over time.
              </p>
            </div>

            <div className="p-6 border border-gray-200 rounded-xl bg-gray-50">
              <h4 className="font-semibold text-gray-900 mb-2">
                Frequent parameter changes
              </h4>
              <p className="text-sm text-gray-600 leading-relaxed">
                Interest rates, benchmarks, fees, and policies change —
                calculations don’t always update as expected.
              </p>
            </div>

            <div className="p-6 border border-gray-200 rounded-xl bg-gray-50">
              <h4 className="font-semibold text-gray-900 mb-2">
                Limited borrower visibility
              </h4>
              <p className="text-sm text-gray-600 leading-relaxed">
                Statements show totals, not logic. Most borrowers never see how
                numbers were derived.
              </p>
            </div>
          </div>

          {/* The Insight */}
          <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                A recurring pattern we couldn't ignore
              </h3>
              <p className="text-sm text-gray-600 max-w-xl mx-auto">
                Across different banks, loan types, and tenures, the same issues
                kept appearing — unnoticed, unquestioned, and unresolved.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-4 mb-8">
              <div className="p-4 bg-gray-50 rounded-lg text-center">
                <p className="text-sm font-medium text-gray-900 mb-1">
                  Rate changes not reflected correctly
                </p>
                <p className="text-xs text-gray-600">
                  Especially in floating-rate loans
                </p>
              </div>

              <div className="p-4 bg-gray-50 rounded-lg text-center">
                <p className="text-sm font-medium text-gray-900 mb-1">
                  Tenure extensions without clarity
                </p>
                <p className="text-xs text-gray-600">
                  EMI remains same, loan stretches longer
                </p>
              </div>

              <div className="p-4 bg-gray-50 rounded-lg text-center">
                <p className="text-sm font-medium text-gray-900 mb-1">
                  Charges buried in statements
                </p>
                <p className="text-xs text-gray-600">
                  Fees and penalties hard to trace
                </p>
              </div>
            </div>

            <div className="p-6 bg-gray-900 text-white rounded-xl text-center">
              <p className="text-base md:text-lg font-semibold leading-relaxed">
                The problem wasn't intent.
                <br />
                <span className="text-indigo-400">
                  It was the absence of an independent verification layer.
                </span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The Mission */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="serif text-3xl md:text-4xl font-bold mb-3 text-gray-900">
              Our mission is simple
            </h2>
            <p className="text-base text-gray-600">
              Give every borrower the clarity they deserve
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <h3 className="serif text-2xl font-semibold mb-4 text-gray-900">
                We do what no human can
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed mb-4">
                Check every transaction. Verify every rate change. Calculate
                what you should have paid vs. what you actually paid.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600 text-xs">
                    ✓
                  </div>
                  <div>
                    <p className="font-medium text-sm text-gray-900">
                      Transaction-level analysis
                    </p>
                    <p className="text-xs text-gray-600">
                      Every EMI, every calculation
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600 text-xs">
                    ✓
                  </div>
                  <div>
                    <p className="font-medium text-sm text-gray-900">
                      Rate change verification
                    </p>
                    <p className="text-xs text-gray-600">
                      Cross-referenced with Bank data
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600 text-xs">
                    ✓
                  </div>
                  <div>
                    <p className="font-medium text-sm text-gray-900">
                      Hidden charge detection
                    </p>
                    <p className="text-xs text-gray-600">
                      Fees you shouldn't have paid
                    </p>
                  </div>
                </li>
              </ul>
            </div>
            <div className="relative">
              <div className="p-6 bg-white rounded-2xl border border-gray-200">
                <div className="space-y-4">
                  <div className="flex items-center justify-between pb-3 border-b border-gray-200">
                    <span className="text-xs text-gray-600">
                      Documents analyzed
                    </span>
                    <span className="text-xl font-semibold text-indigo-600">
                      10,000+
                    </span>
                  </div>
                  <div className="flex items-center justify-between pb-3 border-b border-gray-200">
                    <span className="text-xs text-gray-600">
                      Overcharges found
                    </span>
                    <span className="text-xl font-semibold text-indigo-600">
                      78%
                    </span>
                  </div>
                  <div className="flex items-center justify-between pb-3 border-b border-gray-200">
                    <span className="text-xs text-gray-600">
                      Average recovery
                    </span>
                    <span className="text-xl font-semibold text-indigo-600">
                      ₹3.2L
                    </span>
                  </div>
                  <div className="pt-2">
                    <p className="text-xs text-gray-500 italic">
                      Data from last 12 months
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* The Promise */}
          <div className="text-center">
            <div className="inline-block p-8 bg-indigo-50 rounded-2xl border border-indigo-100">
              <h3 className="serif text-xl font-semibold mb-3 text-gray-900">
                Our promise to you
              </h3>
              <p className="text-sm text-gray-700 max-w-xl mx-auto leading-relaxed">
                We're not here to create conflict. We're here to create{" "}
                <span className="text-indigo-600 font-medium">clarity</span>. If
                everything is fair — peace of mind. If something's wrong —
                evidence to fix it.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how" className="py-20 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="serif text-3xl md:text-4xl font-bold mb-3 text-gray-900">
              How it works
            </h2>
            <p className="text-base text-gray-600">
              Three simple steps. Complete transparency.
            </p>
          </div>

          <div className="space-y-16">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div className="order-2 md:order-1">
                <div className="p-6 bg-white rounded-2xl border border-gray-200">
                  <div className="w-full h-48 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl flex items-center justify-center">
                    <span className="text-5xl">📄</span>
                  </div>
                </div>
              </div>
              <div className="order-1 md:order-2">
                <div className="inline-block px-3 py-1 bg-blue-50 rounded-full text-xs font-semibold text-blue-600 mb-3">
                  STEP 1
                </div>
                <h3 className="serif text-2xl font-semibold mb-3 text-gray-900">
                  Upload your documents
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed mb-4">
                  Loan agreement, EMI statements, payment receipts — anything
                  that shows what you've paid.
                </p>
                <ul className="space-y-2 mb-4">
                  <li className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs">
                      ✓
                    </div>
                    <span className="text-sm text-gray-700">
                      Bank statements
                    </span>
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs">
                      ✓
                    </div>
                    <span className="text-sm text-gray-700">
                      Loan sanction letter
                    </span>
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs">
                      ✓
                    </div>
                    <span className="text-sm text-gray-700">
                      Payment receipts
                    </span>
                  </li>
                </ul>
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-xs text-gray-600">
                    🔒{" "}
                    <span className="font-medium">Bank-grade encryption</span> —
                    Secure & deleted after analysis
                  </p>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <div className="inline-block px-3 py-1 bg-purple-50 rounded-full text-xs font-semibold text-purple-600 mb-3">
                  STEP 2
                </div>
                <h3 className="serif text-2xl font-semibold mb-3 text-gray-900">
                  We calculate everything
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed mb-4">
                  Our system reconstructs your entire loan journey. Month by
                  month. Transaction by transaction.
                </p>
                <div className="space-y-3">
                  <div className="p-3 bg-white rounded-lg border border-gray-200">
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-sm text-gray-900">
                        Interest rate accuracy
                      </span>
                      <span className="text-purple-600 font-medium text-xs">
                        Verified ✓
                      </span>
                    </div>
                  </div>
                  <div className="p-3 bg-white rounded-lg border border-gray-200">
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-sm text-gray-900">
                        EMI calculation method
                      </span>
                      <span className="text-purple-600 font-medium text-xs">
                        Checked ✓
                      </span>
                    </div>
                  </div>
                  <div className="p-3 bg-white rounded-lg border border-gray-200">
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-sm text-gray-900">
                        Penalty validation
                      </span>
                      <span className="text-purple-600 font-medium text-xs">
                        Verified ✓
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <div className="p-6 bg-gradient-to-br from-purple-50 to-indigo-50 rounded-2xl border border-purple-100">
                  <div className="w-full h-48 flex items-center justify-center">
                    <span className="text-5xl">🧮</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div className="order-2 md:order-1">
                <div className="p-6 bg-white rounded-2xl border border-gray-200">
                  <div className="space-y-4">
                    <div className="text-center pb-4 border-b border-gray-200">
                      <h4 className="text-lg font-semibold mb-1 text-gray-900">
                        Your Loan Analysis
                      </h4>
                      <p className="text-xs text-gray-500">
                        Complete breakdown
                      </p>
                    </div>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">
                          Total paid
                        </span>
                        <span className="font-semibold text-gray-900">
                          ₹82,40,000
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">
                          Should have paid
                        </span>
                        <span className="font-semibold text-indigo-600">
                          ₹78,60,000
                        </span>
                      </div>
                      <div className="pt-3 border-t-2 border-indigo-600">
                        <div className="flex justify-between items-center">
                          <span className="font-semibold text-sm text-gray-900">
                            Overcharged
                          </span>
                          <span className="text-xl font-bold text-indigo-600">
                            ₹3,80,000
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="order-1 md:order-2">
                <div className="inline-block px-3 py-1 bg-indigo-50 rounded-full text-xs font-semibold text-indigo-600 mb-3">
                  STEP 3
                </div>
                <h3 className="serif text-2xl font-semibold mb-3 text-gray-900">
                  Get your detailed report
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed mb-4">
                  A complete breakdown. Clear, detailed, backed by your
                  documents.
                </p>
                <ul className="space-y-3 mb-4">
                  <li className="flex items-start gap-2">
                    <div className="flex-shrink-0 w-5 h-5 bg-indigo-600 rounded-full flex items-center justify-center text-white text-xs">
                      1
                    </div>
                    <div>
                      <p className="font-medium text-sm text-gray-900">
                        What should have been paid
                      </p>
                      <p className="text-xs text-gray-600">
                        Based on your loan terms
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="flex-shrink-0 w-5 h-5 bg-indigo-600 rounded-full flex items-center justify-center text-white text-xs">
                      2
                    </div>
                    <div>
                      <p className="font-medium text-sm text-gray-900">
                        What you actually paid
                      </p>
                      <p className="text-xs text-gray-600">
                        From your statements
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="flex-shrink-0 w-5 h-5 bg-indigo-600 rounded-full flex items-center justify-center text-white text-xs">
                      3
                    </div>
                    <div>
                      <p className="font-medium text-sm text-gray-900">
                        The difference (if any)
                      </p>
                      <p className="text-xs text-gray-600">
                        With evidence for discrepancies
                      </p>
                    </div>
                  </li>
                </ul>
                <div className="p-4 bg-indigo-50 border-l-4 border-indigo-600 rounded-r-lg">
                  <p className="font-medium text-xs mb-1 text-gray-900">
                    Then what?
                  </p>
                  <p className="text-xs text-gray-700">
                    You decide. Approach your bank, file a complaint, or have
                    peace of mind.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="serif text-3xl md:text-4xl font-bold mb-3 text-gray-900">
              Real people. Real results.
            </h2>
            <p className="text-base text-gray-600">
              Actual families we've helped
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="p-6 bg-white rounded-xl border border-gray-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-indigo-400 to-purple-400 rounded-full" />
                <div>
                  <h4 className="font-semibold text-sm text-gray-900">
                    Anjali K.
                  </h4>
                  <p className="text-xs text-gray-500">Bangalore • 12 years</p>
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-4 italic">
                "I thought I was imagining things. LoanTruth proved I wasn't
                crazy."
              </p>
              <div className="pt-4 border-t border-gray-100">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-500">Recovered</span>
                  <span className="text-lg font-semibold text-indigo-600">
                    ₹2.8L
                  </span>
                </div>
              </div>
            </div>

            <div className="p-6 bg-white rounded-xl border border-gray-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-cyan-400 rounded-full" />
                <div>
                  <h4 className="font-semibold text-sm text-gray-900">
                    Vikram S.
                  </h4>
                  <p className="text-xs text-gray-500">Mumbai • 18 years</p>
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-4 italic">
                "₹5.4 lakhs overcharged. We used it for my daughter's
                education."
              </p>
              <div className="pt-4 border-t border-gray-100">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-500">Recovered</span>
                  <span className="text-lg font-semibold text-indigo-600">
                    ₹5.4L
                  </span>
                </div>
              </div>
            </div>

            <div className="p-6 bg-white rounded-xl border border-gray-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-orange-400 rounded-full" />
                <div>
                  <h4 className="font-semibold text-sm text-gray-900">
                    Meera P.
                  </h4>
                  <p className="text-xs text-gray-500">Pune • 8 years</p>
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-4 italic">
                "Even though my bank said everything was fine, the numbers told
                a different story."
              </p>
              <div className="pt-4 border-t border-gray-100">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-500">Recovered</span>
                  <span className="text-lg font-semibold text-indigo-600">
                    ₹1.9L
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-12 text-center">
            <div className="inline-block p-6 bg-indigo-50 rounded-xl border border-indigo-100">
              <p className="text-2xl font-bold mb-1 text-gray-900">
                ₹32+ Crores
              </p>
              <p className="text-sm text-gray-600">
                Total recovered for families
              </p>
            </div>
          </div>
        </div>
      </section>

      {/*  CTA */}
      <section className="py-20 px-6 bg-gradient-to-b from-white to-indigo-50/30">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="serif text-3xl md:text-4xl font-bold mb-4 leading-tight text-gray-900">
              Your  loan
              <br />
              <span className="text-indigo-600">deserves a second look</span>
            </h2>
            <p className="text-base text-gray-600 leading-relaxed max-w-2xl mx-auto">
              You worked hard for your home. You paid every month. Let us verify
              that trust was honored.
            </p>
          </div>

          <div className="text-center mb-8">
            <button
              onClick={() => console.log("Navigate to upload")}
              className="inline-flex items-center gap-3 px-8 py-3 bg-indigo-600 text-white rounded-lg text-base font-semibold hover:bg-indigo-700 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              <span>Check My Loan For Free</span>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M5 12H19M19 12L12 5M19 12L12 19"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-6 text-gray-500 mb-12 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-indigo-500 rounded-full" />
              <span>Free analysis</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-indigo-500 rounded-full" />
              <span>100% confidential</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-indigo-500 rounded-full" />
              <span>No obligation</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-indigo-500 rounded-full" />
              <span>Results in 48 hours</span>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="p-6 bg-white rounded-xl border border-gray-200">
              <div className="text-2xl mb-3">✅</div>
              <h3 className="text-lg font-semibold mb-2 text-gray-900">
                If everything's correct
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                Peace of mind. Your trust was well-placed. Walk away knowing you
                did right.
              </p>
            </div>
            <div className="p-6 bg-indigo-50 rounded-xl border border-indigo-100">
              <div className="text-2xl mb-3">💪</div>
              <h3 className="text-lg font-semibold mb-2 text-gray-900">
                If something's wrong
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                Evidence. Clear documentation. The power to claim what's
                rightfully yours.
              </p>
            </div>
          </div>

          <div className="mt-12 p-8 bg-gray-900 text-white rounded-2xl text-center">
            <p className="text-lg serif font-semibold leading-relaxed">
              "Every family that worked hard for their home
              <br />
              deserves to know the{" "}
              <span className="text-indigo-400">truth</span>."
            </p>
            <p className="mt-4 text-gray-400 text-sm">— The LoanTruth Team</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      {/* <footer className="py-12 px-6 border-t border-gray-200">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div className="md:col-span-2">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 bg-indigo-500 rounded-lg flex items-center justify-center text-white text-sm font-semibold">
                  L
                </div>
                <span className="text-lg font-semibold text-gray-900">
                  LoanTruth
                </span>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed mb-3">
                Born from a personal story. Built for every family that trusted
                their bank.
              </p>
              <p className="text-xs text-gray-500">
                Because financial justice shouldn't be a luxury.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-3 text-sm text-gray-900">
                Quick Links
              </h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>
                  <a href="#why" className="hover:text-indigo-600 transition">
                    Why We Exist
                  </a>
                </li>
                <li>
                  <a href="#how" className="hover:text-indigo-600 transition">
                    How It Works
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-indigo-600 transition">
                    Success Stories
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-indigo-600 transition">
                    FAQ
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-3 text-sm text-gray-900">
                Legal
              </h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>
                  <a href="#" className="hover:text-indigo-600 transition">
                    Privacy Policy
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-indigo-600 transition">
                    Terms of Service
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-indigo-600 transition">
                    Security
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-indigo-600 transition">
                    Contact
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div className="pt-6 border-t border-gray-200 text-center text-gray-500 text-xs">
            <p>© {new Date().getFullYear()} LoanTruth. Built with purpose.</p>
          </div>
        </div>
      </footer> */}
    </div>
  );
}
